import { computed, Injectable, signal } from '@angular/core';

/**
 * FlowStep represents a step in the application flow the user can reach.
 */
export type FlowStep =
  | 'verify-otp'
  | 'application'
  | 'application-denial'
  | 'application-decision'
  | 'accepted'
  | 'declined'
  | 'cancel'
  | 'warning';
//| 'begin'

/** FlowPath represents a path a user can take in the system (application-only in standalone app) */
export type FlowPath = 'application-flow';

/** 
 * FlowState holds the current state of the user moving through the system
 */
interface FlowState {
  completedSteps: Set<FlowStep>;
  currentStep: FlowStep | null;
  activePath: FlowPath | null;
}

@Injectable({
  providedIn: 'root'
})
export class FlowStateService {
  // Record the current flow state
  private flowState = signal<FlowState>({
    completedSteps: new Set(),
    currentStep: null,
    activePath: null,
  });

  // Export completed steps and the current step
  readonly completedSteps = computed(() => this.flowState().completedSteps);
  readonly currentStep = computed(() => this.flowState().currentStep);
  readonly activePath = computed(() => this.flowState().activePath);

  /**
   * Mark a step as completed and set it as current
   * @param step The step to complete
   * @param path The path this step is located on
   */
  completeStep(step: FlowStep, path?: FlowPath): void {
    this.flowState.update(state => ({
      completedSteps: new Set([...state.completedSteps, step]),
      currentStep: step,
      activePath: path || state.activePath,
    }));
  }

  /**
   * Check if a step has been completed
   * @param step The step to check
   * @return true if completed, false if not
   */
  hasCompletedStep(step: FlowStep): boolean {
    return this.flowState().completedSteps.has(step);
  }

  /**
   * Reset the flow state
   */
  reset(): void {
    this.flowState.set({
      completedSteps: new Set(),
      currentStep: null,
      activePath: null,
    });
  }
}